// Helpers for tab groups whose ids must be DERIVED from human labels (data-driven tabs with
// no ids of their own). Most tab groups hand-author stable kebab ids and don't need this —
// `useTabUrlState` stays purely id-based; this is only for turning free-text labels into
// URL-safe ids without each consumer re-implementing it.

// URL-safe kebab id for a single label, e.g. "Social Media" -> "social-media".
export function slugifyTabId(label) {
  return String(label ?? '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Build `{ id, label, value }` tab descriptors from raw labels: `id` is a unique kebab slug
 * (for the URL), `label` is the original text (display), `value` is what the consumer filters
 * on (the label). Slug collisions get a `-2`, `-3`… suffix so ids stay unique.
 *
 * @param {string[]} labels
 * @param {{ leading?: { id: string, label: string, value: * } }} [options] optional first tab
 *   (e.g. an "All" entry whose value differs from its label).
 * @returns {{ id: string, label: string, value: * }[]}
 */
export function buildTabsFromLabels(labels, { leading } = {}) {
  const used = new Set();
  const uniqueId = base => {
    const root = base || 'tab';
    let id = root;
    let n = 2;
    while (used.has(id)) id = `${root}-${n++}`;
    used.add(id);
    return id;
  };

  const tabs = [];
  if (leading) {
    tabs.push({
      id: uniqueId(leading.id),
      label: leading.label,
      value: leading.value
    });
  }
  (labels || []).forEach(label => {
    tabs.push({ id: uniqueId(slugifyTabId(label)), label, value: label });
  });
  return tabs;
}
